import { useDisplayHeartbeat } from './display-heartbeat-runtime.ts'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { Outlet, type AnyRouter } from '@tanstack/react-router'
import {
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
  type JSX
} from 'react'

import {
  DISPLAY_REFRESH_MS,
  advanceSession,
  currentDisplayPage,
  dispatchDisplay,
  displayNameFromPath,
  getDisplayClock,
  getDisplaySession,
  isDisplayActive,
  markMissing,
  markOffline,
  msUntilAdvance,
  playlistReady,
  playlistUnavailable,
  retrySession,
  startSession,
  subscribeDisplay,
  type DisplaySession
} from '../lib/display-mode.ts'
import { heartbeatReportKey } from '../lib/display-heartbeat.ts'
import { loadDisplayPlaylist } from '../lib/display-playlist.ts'
import { isTvFullscreenPath, type InternalPage } from '../lib/pages.ts'
import { holdWakeLock } from '../lib/wake-lock.ts'
import { ReadOnlyProvider } from './read-only.tsx'
import { TvCanvas } from './tv-canvas.tsx'
import { TvHeaderBar } from './tv-header-bar.tsx'

const refreshScopes = new WeakMap<
  QueryClient,
  { count: number; previous: ReturnType<QueryClient['getDefaultOptions']> }
>()

/**
 * The display session, for a component.
 *
 * @returns The session now.
 */
export function useDisplaySession(): DisplaySession {
  return useSyncExternalStore(
    subscribeDisplay,
    getDisplaySession,
    getDisplaySession
  )
}

/**
 * Whether the shell should be the display, not the console.
 *
 * True on `/display/<name>` before the session has started, and true after
 * the rotator has navigated to a page of its own. Either one hides the chrome.
 *
 * @param router - The app router.
 * @returns True while a display owns the screen.
 */
export function useDisplaying(router: AnyRouter): boolean {
  return useSyncExternalStore(
    onStoreChange => {
      const unsubRouter = router.subscribe('onResolved', () => {
        onStoreChange()
      })
      const unsubSession = subscribeDisplay(onStoreChange)

      return () => {
        unsubRouter()
        unsubSession()
      }
    },
    () =>
      isDisplayActive(getDisplaySession()) ||
      displayNameFromPath(
        router.state.location.maskedLocation?.pathname ??
          router.state.location.pathname
      ) !== null,
    () => false
  )
}

/**
 * DisplayQueryRefresh — refetch every query on the display's minute.
 *
 * Set in a layout effect. The previous default returns after the last display
 * or visible preview stops. A query with its own `refetchInterval` keeps it.
 *
 * @param props - Whether a display or preview is running.
 * @param props.active - True while the frame is visible.
 * @returns Nothing.
 */
export function DisplayQueryRefresh({ active }: { active: boolean }): null {
  const client = useQueryClient()

  useLayoutEffect(() => {
    if (!active) return undefined
    let scope = refreshScopes.get(client)
    if (!scope) {
      const previous = client.getDefaultOptions()
      scope = { count: 0, previous }
      refreshScopes.set(client, scope)
      const queries = previous.queries ?? {}
      client.setDefaultOptions({
        ...previous,
        queries: { ...queries, refetchInterval: DISPLAY_REFRESH_MS }
      })
    }
    scope.count += 1

    return () => {
      scope.count -= 1
      if (scope.count === 0) {
        client.setDefaultOptions(scope.previous)
        refreshScopes.delete(client)
      }
    }
  }, [active, client])

  return null
}

const reloadWindow = (): void => window.location.reload()

const NO_PAGES: readonly InternalPage[] = []

/**
 * DisplayRuntime — load the playlist and turn its pages.
 *
 * Mounted beside the router, not inside a page. A page that throws is the
 * router's error, and the rotation has to keep going. Route masking keeps
 * `/display/<name>` in the address bar while each page renders at its own path.
 *
 * The playlist is fetched again when the last page's time is up, so an edit
 * on the Displays page lands on the next cycle.
 *
 * @param props - The router whose history the pages are shown on.
 * @param props.router - The app router.
 * @param props.reload - Reloads the window, injected by tests.
 * @returns Nothing.
 */
export function DisplayRuntime({
  router,
  reload = reloadWindow
}: {
  router: AnyRouter
  reload?: () => void
}): null {
  const session = useDisplaySession()
  const reportKey = heartbeatReportKey(session)
  const active = isDisplayActive(session)

  useDisplayHeartbeat(reportKey, reload)

  useEffect(() => {
    if (active) return holdWakeLock(navigator, document)
    return undefined
  }, [active])

  useEffect(() => {
    let cancelled = false
    const clock = getDisplayClock()
    const page = currentDisplayPage(session)
    if (
      session.kind !== 'off' &&
      page !== null &&
      router.state.location.href !== page.path
    ) {
      const target = new URL(page.path, window.location.origin)
      void router.navigate({
        to: target.pathname,
        search: router.options.parseSearch(target.search),
        hash: target.hash.slice(1),
        mask: { to: '/display/$name', params: { name: session.name } },
        replace: true
      })
    }

    if (session.kind === 'booting' || session.kind === 'refreshing') {
      const requested = session.name
      const kind = session.kind
      void loadDisplayPlaylist(requested).then(result => {
        if (cancelled) {
          return
        }
        const current = getDisplaySession()
        if (current.kind !== kind || current.name !== requested) {
          return
        }
        const now = getDisplayClock().now()
        if (result.status === 'ready') {
          dispatchDisplay(
            playlistReady(
              current,
              {
                name: result.name,
                pages: result.pages,
                theme: result.theme
              },
              now
            )
          )

          return
        }
        dispatchDisplay(
          result.status === 'missing'
            ? markMissing(current, now)
            : playlistUnavailable(current, now)
        )
      })
    }

    let cancelTimer: (() => void) | undefined
    if (
      session.kind === 'playing' ||
      session.kind === 'offline' ||
      session.kind === 'missing'
    ) {
      const wait = msUntilAdvance(session, clock.now())
      if (wait !== null) {
        const kind = session.kind
        cancelTimer = clock.after(wait, () => {
          const current = getDisplaySession()
          if (current.kind !== kind) {
            return
          }
          const now = getDisplayClock().now()
          dispatchDisplay(
            kind === 'playing'
              ? advanceSession(current, now)
              : retrySession(current)
          )
        })
      }
    }

    return () => {
      cancelled = true
      cancelTimer?.()
    }
  }, [router, session])

  useEffect(() => {
    // The entry route does not mount before access answers, or while the
    // surface shows missing or offline. Start the TV from the runtime.
    const followPath = (): void => {
      const name = displayNameFromPath(
        router.state.location.maskedLocation?.pathname ??
          router.state.location.pathname
      )
      if (name === null) {
        return
      }
      const current = getDisplaySession()
      if (
        current.kind !== 'off' &&
        current.kind !== 'missing' &&
        current.kind !== 'offline'
      ) {
        return
      }
      dispatchDisplay(startSession(current, name))
    }
    followPath()

    return router.subscribe('onResolved', followPath)
  }, [router])

  useEffect(() => {
    const onOffline = (): void => {
      const current = getDisplaySession()
      if (
        current.kind === 'playing' ||
        current.kind === 'refreshing' ||
        current.kind === 'booting'
      ) {
        dispatchDisplay(markOffline(current, getDisplayClock().now()))
      }
    }
    const onOnline = (): void => {
      const current = getDisplaySession()
      if (current.kind === 'offline' || current.kind === 'missing') {
        dispatchDisplay(retrySession(current))
      }
    }
    window.addEventListener('offline', onOffline)
    window.addEventListener('online', onOnline)

    return () => {
      window.removeEventListener('offline', onOffline)
      window.removeEventListener('online', onOnline)
    }
  }, [])

  return null
}

/**
 * The screen a display shows instead of the page: not set up, or offline.
 *
 * A playing display renders the router outlet, or `replacement` when the
 * current page is one the rail would deny. Missing and offline ignore that
 * replacement. The page is the app's own route, never an iframe.
 *
 * A playing display draws the status bar above the page, unless the page
 * declares that it plays full screen.
 *
 * @param props - What to show instead of the outlet.
 * @param props.replacement - The denied state, when there is one.
 * @param props.accessState - Whether the access check permits mounting a page.
 * @param props.pages - The declared page list. The framework cannot import it.
 * @returns The notice, the denied state, or the current page.
 */
export function DisplaySurface({
  replacement = null,
  accessState = 'ready',
  pages = NO_PAGES
}: {
  replacement?: JSX.Element | null
  accessState?: 'loading' | 'error' | 'ready'
  pages?: readonly InternalPage[]
}): JSX.Element {
  const session = useDisplaySession()
  const path = currentDisplayPage(session)?.path
  if (session.kind === 'missing' && accessState === 'ready') {
    return (
      <div
        data-testid="display-missing"
        className="grid min-h-svh place-items-center p-6 text-center"
      >
        <div>
          <h1 data-testid="display-missing-title" className="type-page-title">
            {'This display is not set up.'}
          </h1>
          <p
            data-testid="display-missing-name"
            className="text-muted-foreground"
          >
            {session.name}
          </p>
        </div>
      </div>
    )
  }
  if (session.kind === 'offline' || accessState === 'error') {
    return (
      <div
        data-testid="display-offline"
        className="grid min-h-svh place-items-center p-6 text-center"
      >
        <div>
          <h1 data-testid="display-offline-title" className="type-page-title">
            {'This display is offline.'}
          </h1>
          <p
            data-testid="display-offline-detail"
            className="text-muted-foreground"
          >
            {'It will try again on its own.'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <TvCanvas
      page={path}
      fullscreen={isTvFullscreenPath(pages, path)}
      header={<TvHeaderBar session={session} />}
    >
      <ReadOnlyProvider>
        {accessState === 'loading' ? (
          <p
            data-testid="display-access-loading"
            className="text-muted-foreground"
          >
            Checking page access…
          </p>
        ) : (
          (replacement ?? <Outlet />)
        )}
      </ReadOnlyProvider>
    </TvCanvas>
  )
}

/**
 * Retry a route error at the start of the next display cycle.
 *
 * Mount this hook only in the router error component. Healthy pages keep
 * their mounts and their own animation clocks.
 *
 * @param router - The router whose failed match needs a fresh load.
 */
export function useDisplayErrorRecovery(router: AnyRouter): void {
  useEffect(() => {
    let previous = getDisplaySession()
    return subscribeDisplay(() => {
      const current = getDisplaySession()
      if (previous.kind === 'refreshing' && current.kind === 'playing') {
        void router.invalidate()
      }
      previous = current
    })
  }, [router])
}
